<template>
  <div class="slow-moving">
    <ReportChoices
      label="Sales in the last"
      icon="ti-history"
      :choices="lookbackChoices"
      :active="filters.days"
      @select="filters.days = $event.key"
    />

    <!-- Filters + actions -->
    <div class="rp-toolbar">
      <div class="rp-filters" style="--rp-cols: 3">
        <div class="rp-filter">
          <label>Warehouse</label>
          <FormDropdown v-model="filters.warehouse" :options="warehouseOptions" placeholder="All warehouses" clearable />
        </div>
        <div class="rp-filter">
          <label>Category</label>
          <FormDropdown v-model="filters.category" :options="categoryOptions" placeholder="All categories" clearable />
        </div>
        <div class="rp-filter">
          <label>Subcategory</label>
          <FormDropdown v-model="filters.sub_category" :options="subCategoryOptions" placeholder="All subcategories" clearable />
        </div>
        <div class="rp-filter rp-search">
          <label>Item</label>
          <div class="position-relative">
            <i class="ti ti-search rp-search-icon"></i>
            <input v-model="filters.search" type="text" class="form-control" placeholder="Search name, code, SKU or barcode" />
          </div>
        </div>
      </div>

      <ReportActions
        :loading="loading"
        :exporting="exporting"
        :can-reset="hasActiveFilters"
        :can-print="!!visibleRows.length"
        @reset="resetFilters"
        @export="exportFile"
        @print="printReport"
      />
    </div>

    <div v-if="error" class="alert alert-danger d-flex align-items-center gap-2">
      <i class="ti ti-alert-circle"></i>{{ error }}
    </div>

    <!-- Summary cards -->
    <div class="rp-cards" :class="{ 'is-loading': loading }">
      <button type="button" class="rp-card rp-card-action tone-red" :class="{ selected: movementFilter === 'no_sales' }"
              @click="toggleMovement('no_sales')">
        <span class="rp-card-icon"><i class="ti ti-zzz"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">No Sales</div>
          <div class="rp-card-value text-danger">{{ formatInt(groups.no_sales.count) }}</div>
          <div class="rp-card-sub">{{ formatMoney(groups.no_sales.value) }} · {{ formatQty(groups.no_sales.qty) }} units</div>
        </div>
      </button>
      <button type="button" class="rp-card rp-card-action tone-orange" :class="{ selected: movementFilter === 'slow' }"
              @click="toggleMovement('slow')">
        <span class="rp-card-icon"><i class="ti ti-hourglass-low"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Slow Moving</div>
          <div class="rp-card-value" style="color: #D9771E">{{ formatInt(groups.slow.count) }}</div>
          <div class="rp-card-sub">{{ formatMoney(groups.slow.value) }} · {{ formatQty(groups.slow.qty) }} units</div>
        </div>
      </button>
      <div class="rp-card tone-navy">
        <span class="rp-card-icon"><i class="ti ti-lock-dollar"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Value Tied Up</div>
          <div class="rp-card-value">{{ formatMoney(summary.stuck_value) }}</div>
          <div class="rp-card-sub">{{ summary.stuck_share }}% of {{ formatMoney(summary.total_value) }} in stock</div>
        </div>
      </div>
      <div class="rp-card tone-blue">
        <span class="rp-card-icon"><i class="ti ti-clock-hour-4"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Longest Idle</div>
          <div class="rp-card-value">{{ summary.oldest ? plural(summary.oldest.idle_days, 'day') : '—' }}</div>
          <div class="rp-card-sub text-truncate" :title="summary.oldest?.name">{{ summary.oldest?.name || 'Everything is selling' }}</div>
        </div>
      </div>
    </div>

    <!-- Insights -->
    <div class="rp-insights" :class="{ 'is-loading': loading }">
      <div class="rp-panel">
        <div class="rp-panel-title">Value by idle time</div>
        <div class="rp-bars">
          <div v-for="bucket in idleBars" :key="bucket.label">
            <div class="d-flex justify-content-between small mb-1">
              <span>{{ bucket.label }}</span>
              <span class="fw-semibold">{{ formatMoney(bucket.value) }}</span>
            </div>
            <div class="rp-bar-track"><div class="rp-bar-fill" :class="bucket.tone" :style="{ width: `${bucket.percent}%` }"></div></div>
          </div>
        </div>
        <div class="rp-note mt-3">Idle = days since the last sale, or since the stock arrived if it never sold.</div>
      </div>

      <div class="rp-panel">
        <div class="rp-panel-title">Tied-up value by category</div>
        <div v-if="categoryBars.length" class="rp-bars">
          <div v-for="category in categoryBars" :key="category.name">
            <div class="d-flex justify-content-between small mb-1">
              <span class="text-truncate me-2">{{ category.name }}</span>
              <span class="fw-semibold">{{ formatMoney(category.value) }}</span>
            </div>
            <div class="rp-bar-track"><div class="rp-bar-fill" :style="{ width: `${category.percent}%` }"></div></div>
          </div>
        </div>
        <p v-else class="text-muted small mb-0">No slow-moving stock.</p>
      </div>

      <div class="rp-panel">
        <div class="rp-panel-title">Biggest value tied up</div>
        <ul v-if="summary.top_stuck.length" class="rp-ranked">
          <li v-for="(item, index) in summary.top_stuck" :key="item.name">
            <span class="rp-rank">{{ index + 1 }}</span>
            <div class="flex-grow-1 min-w-0">
              <div class="text-truncate" :title="item.name">{{ item.name }}</div>
              <small class="text-muted">{{ formatMoney(item.value) }} · idle {{ plural(item.idle_days, 'day') }}</small>
            </div>
            <span class="rp-badge" :class="item.movement === 'no_sales' ? 'danger' : 'warning'">
              {{ item.movement === 'no_sales' ? 'No sales' : 'Slow' }}
            </span>
          </li>
        </ul>
        <p v-else class="text-muted small mb-0">No slow-moving stock.</p>
      </div>
    </div>

    <!-- Table -->
    <div class="rp-table-card">
      <div class="rp-table-head">
        <div class="rp-pills">
          <button v-for="pill in movementPills" :key="pill.key" type="button" class="rp-pill"
                  :class="{ active: movementFilter === pill.key }" @click="movementFilter = pill.key">
            {{ pill.label }} <span class="rp-pill-count">{{ formatInt(pill.count) }}</span>
          </button>
        </div>
        <div class="d-flex align-items-center gap-2 small text-muted">
          Show
          <select v-model.number="pageSize" class="form-select form-select-sm rp-page-size">
            <option v-for="size in PAGE_SIZES" :key="size" :value="size">{{ size }}</option>
          </select>
          per page
        </div>
      </div>

      <div class="table-responsive rp-table-wrap">
        <div v-if="loading" class="rp-loading"><span class="spinner-border text-warning"></span></div>
        <table class="table rp-table mb-0">
          <thead>
            <tr>
              <th v-for="column in columns" :key="column.key" :class="column.class" :title="column.title" @click="sortBy(column.key)">
                <span class="rp-sortable" :class="{ active: sortKey === column.key }">
                  {{ column.label }}
                  <i class="ti" :class="sortIcon(column.key)"></i>
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in pagedRows" :key="row.key">
              <td class="fw-semibold">{{ row.item_code }}</td>
              <td>
                <div class="fw-medium">{{ row.item_name }}</div>
                <span v-if="row.sku" class="rp-sub">SKU: {{ row.sku }}</span>
              </td>
              <td>
                <template v-if="row.variant">
                  <span v-for="part in row.variant.split(' · ')" :key="part" class="rp-variant">{{ part }}</span>
                </template>
                <span v-else class="text-muted">—</span>
              </td>
              <td>{{ row.warehouse }}</td>
              <td class="text-end">{{ formatQty(row.available_qty) }}<small v-if="row.unit" class="text-muted ms-1">{{ row.unit }}</small></td>
              <td class="text-end fw-semibold">{{ formatMoney(row.stock_value) }}</td>
              <td class="text-end">{{ Number(row.sold_qty) ? formatQty(row.sold_qty) : '—' }}</td>
              <td class="text-nowrap">{{ row.last_sale ? formatDate(row.last_sale) : 'Never' }}</td>
              <td class="text-end">{{ row.idle_days ?? '—' }}</td>
              <td class="text-end">{{ row.stock_age ?? '—' }}</td>
              <td class="text-end">{{ row.cover_days === null ? '∞' : formatInt(row.cover_days) }}</td>
              <td>
                <span class="rp-badge" :class="{ no_sales: 'danger', slow: 'warning', moving: 'success' }[row.movement]">
                  {{ row.movement_label }}
                </span>
              </td>
            </tr>

            <tr v-if="!loading && !pagedRows.length">
              <td :colspan="columns.length">
                <div class="rp-empty">
                  <i class="ti ti-rocket"></i>
                  <div class="fw-semibold">No slow-moving stock</div>
                  <small class="text-muted">Everything in stock sold well in the last {{ summary.days }} days.</small>
                </div>
              </td>
            </tr>
          </tbody>
          <tfoot v-if="visibleRows.length">
            <tr>
              <th colspan="4" class="text-end">Total ({{ plural(visibleRows.length, 'item') }})</th>
              <th class="text-end">{{ formatQty(visibleTotals.available_qty) }}</th>
              <th class="text-end">{{ formatMoney(visibleTotals.stock_value) }}</th>
              <th class="text-end">{{ formatQty(visibleTotals.sold_qty) }}</th>
              <th colspan="5"></th>
            </tr>
          </tfoot>
        </table>
      </div>

      <ReportPagination
        v-if="visibleRows.length"
        :page="page"
        :page-count="pageCount"
        :pages="pageNumbers"
        :start="pageStart"
        :end="pageEnd"
        :total="visibleRows.length"
        @go="goToPage"
      />
    </div>
    <p class="rp-hint mt-2 mb-0">
      Slow = sold in the last {{ summary.days }} days, but at that rate the stock on hand lasts longer than
      {{ summary.days }} days. Days of cover = stock ÷ average daily sales.
    </p>
  </div>
</template>

<script>
import apiUrls from '@/urls';
import inventoryReport, { escapeHtml, localToday } from './inventoryReportMixin';

const DEFAULT_DAYS = 90;
const EMPTY_GROUP = { count: 0, qty: 0, value: 0 };
const EMPTY_SUMMARY = {
  days: DEFAULT_DAYS, groups: { no_sales: EMPTY_GROUP, slow: EMPTY_GROUP, moving: EMPTY_GROUP }, total_value: 0,
  stuck_value: 0, stuck_share: 0, stuck_count: 0, oldest: null, idle_buckets: [], by_category: [], top_stuck: [],
};
const IDLE_TONES = ['success', 'info', 'warning', 'danger', 'danger'];

export default {
  name: 'SlowMovingReport',
  mixins: [inventoryReport],

  reportUrl: apiUrls.slow_moving_report.get,
  fileName: 'slow_moving_stock',
  columns: [
    { key: 'item_code', label: 'Item Code' },
    { key: 'item_name', label: 'Item Name' },
    { key: 'variant', label: 'Variant' },
    { key: 'warehouse', label: 'Warehouse' },
    { key: 'available_qty', label: 'In Stock', class: 'text-end', numeric: true },
    { key: 'stock_value', label: 'Stock Value', class: 'text-end', numeric: true },
    { key: 'sold_qty', label: 'Sold', class: 'text-end', numeric: true, title: 'Net quantity sold in the look-back period' },
    { key: 'last_sale', label: 'Last Sale' },
    { key: 'idle_days', label: 'Idle Days', class: 'text-end', numeric: true },
    { key: 'stock_age', label: 'Stock Age', class: 'text-end', numeric: true, title: 'Days since the oldest batch in stock arrived' },
    { key: 'cover_days', label: 'Cover Days', class: 'text-end', numeric: true, title: 'How many days the stock lasts at the current sales rate' },
    { key: 'movement_label', label: 'Movement' },
  ],

  data() {
    return {
      filters: { days: DEFAULT_DAYS, warehouse: null, category: null, sub_category: null, search: '' },
      summary: { ...EMPTY_SUMMARY },
      movementFilter: 'stuck',
      sortKey: null,
      sortDir: 'asc',
      lookbackChoices: [30, 60, 90, 180, 365].map((days) => ({ key: days, label: `${days} days` })),
    };
  },

  computed: {
    groups() {
      return { ...EMPTY_SUMMARY.groups, ...this.summary.groups };
    },

    hasActiveFilters() {
      return this.itemFiltersActive || this.filters.days !== DEFAULT_DAYS || this.movementFilter !== 'stuck';
    },

    movementPills() {
      const g = this.groups;
      return [
        { key: 'stuck', label: 'Slow & no sales', count: g.no_sales.count + g.slow.count },
        { key: 'no_sales', label: 'No sales', count: g.no_sales.count },
        { key: 'slow', label: 'Slow', count: g.slow.count },
        { key: 'all', label: 'All in stock', count: this.rows.length },
      ];
    },

    idleBars() {
      return this.withPercent(this.summary.idle_buckets, 'value').map((b, i) => ({ ...b, tone: IDLE_TONES[i] }));
    },

    categoryBars() {
      return this.withPercent(this.summary.by_category, 'value');
    },

    visibleRows() {
      const keep = {
        stuck: (r) => r.movement !== 'moving',
        no_sales: (r) => r.movement === 'no_sales',
        slow: (r) => r.movement === 'slow',
      }[this.movementFilter];
      const rows = keep ? this.rows.filter(keep) : this.rows;
      return this.sortKey ? this.sortRows(rows) : rows;
    },

    visibleTotals() {
      const fields = ['available_qty', 'stock_value', 'sold_qty'];
      return this.visibleRows.reduce((totals, row) => {
        fields.forEach((field) => { totals[field] += Number(row[field]) || 0; });
        return totals;
      }, Object.fromEntries(fields.map((field) => [field, 0])));
    },
  },

  watch: {
    'filters.days'() { this.onFilterChange(); },
    movementFilter() { this.page = 1; },
  },

  methods: {
    extraParams() {
      return { days: this.filters.days };
    },

    resetExtraFilters() {
      this.movementFilter = 'stuck';
      this.sortKey = null;
      return { days: DEFAULT_DAYS };
    },

    applyResult(data) {
      this.summary = { ...EMPTY_SUMMARY, ...(data?.summary || {}) };
    },

    toggleMovement(movement) {
      this.movementFilter = this.movementFilter === movement ? 'stuck' : movement;
    },

    printReport() {
      const s = this.summary;
      const body = this.visibleRows.map((r) => `
        <tr>
          <td>${escapeHtml(r.item_code)}</td>
          <td>${escapeHtml(r.item_name)}</td>
          <td>${escapeHtml(r.variant)}</td>
          <td>${escapeHtml(r.warehouse)}</td>
          <td class="num">${this.formatQty(r.available_qty)}</td>
          <td class="num">${this.formatMoney(r.stock_value)}</td>
          <td class="num">${this.formatQty(r.sold_qty)}</td>
          <td>${r.last_sale ? this.formatDate(r.last_sale) : 'Never'}</td>
          <td class="num">${r.idle_days ?? ''}</td>
          <td class="num">${r.cover_days === null ? '∞' : this.formatInt(r.cover_days)}</td>
          <td class="${r.movement === 'no_sales' ? 'out_of_stock' : 'low_stock'}">${escapeHtml(r.movement_label)}</td>
        </tr>`).join('');
      const t = this.visibleTotals;
      const table = `<table><thead><tr><th>Item Code</th><th>Item Name</th><th>Variant</th><th>Warehouse</th>
          <th class="num">In Stock</th><th class="num">Stock Value</th><th class="num">Sold</th><th>Last Sale</th>
          <th class="num">Idle Days</th><th class="num">Cover Days</th><th>Movement</th></tr></thead>
        <tbody>${body}</tbody>
        <tfoot><tr><td colspan="4" class="num">Total</td><td class="num">${this.formatQty(t.available_qty)}</td>
          <td class="num">${this.formatMoney(t.stock_value)}</td><td class="num">${this.formatQty(t.sold_qty)}</td>
          <td colspan="4"></td></tr></tfoot></table>`;
      this.openPrint(
        'Slow Moving Stock',
        [[`As on: ${this.formatDate(localToday())}`, `Sales in the last ${s.days} days`, ...this.itemFiltersText()].join(' · '),
          `Printed ${new Date().toLocaleString()}`],
        [
          ['No Sales', `${this.plural(this.groups.no_sales.count, 'item')} · ${this.formatMoney(this.groups.no_sales.value)}`],
          ['Slow Moving', `${this.plural(this.groups.slow.count, 'item')} · ${this.formatMoney(this.groups.slow.value)}`],
          ['Value Tied Up', `${this.formatMoney(s.stuck_value)} (${s.stuck_share}%)`],
          ['Longest Idle', s.oldest ? this.plural(s.oldest.idle_days, 'day') : '—'],
        ],
        table,
      );
    },
  },
};
</script>

<style scoped src="./inventoryReport.css"></style>
